import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as ManageIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import OrganizationCreateDialog from '../../components/admin/OrganizationCreateDialog';
import OrganizationEditDialog from '../../components/admin/OrganizationEditDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LoadingScreen } from '../../components/common/LoadingScreen';

interface Country {
  code: string;
  code_alpha3: string;
  code_numeric: string;
  name: string;
  name_official: string;
}

interface Organization {
  id: number;
  name: string;
  owner_uuid: string;
  primary_country_code: string;
  created_at: string;
  updated_at: string;
  primary_country: Country;
  owner_name: string;
  owner_email: string;
}

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const orgsData = await apiClient.get<Organization[]>('/admin/organizations');
      setOrganizations(orgsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organisations');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEditOrganization = (org: Organization) => {
    setSelectedOrganization(org);
    setEditDialogOpen(true);
  };

  const handleDeleteOrganization = (org: Organization) => {
    setSelectedOrganization(org);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrganization) return;

    try {
      await apiClient.delete(`/admin/organizations/${selectedOrganization.id}`);
      await loadOrganizations();
      setDeleteDialogOpen(false);
      setSelectedOrganization(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organisation');
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedOrganization(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedOrganization(null);
  };

  if (loading) {
    return <LoadingScreen message="Loading organisations..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            Error loading organisations: {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <BusinessIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Organisation Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="small"
          >
            Add Organisation
          </Button>
        </Box>

        {/* Organizations Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow
                  key={org.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/organizations/${org.id}`)}
                >
                  <TableCell>{org.name}</TableCell>
                  <TableCell>
                    {org.owner_name}
                    <Typography variant="body2" color="text.secondary">
                      {org.owner_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {org.primary_country.name} ({org.primary_country.code})
                  </TableCell>
                  <TableCell>{formatDate(org.created_at)}</TableCell>
                  <TableCell>{formatDate(org.updated_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/admin/organizations/${org.id}`); }}
                      title="Manage organisation"
                    >
                      <ManageIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleEditOrganization(org); }}
                      title="Edit organisation"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeleteOrganization(org); }}
                      title="Delete organisation"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {organizations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No organisations found
            </Typography>
          </Box>
        )}

        {/* Dialogs */}
        <OrganizationCreateDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onOrganizationCreated={loadOrganizations}
        />

        <OrganizationEditDialog
          organization={selectedOrganization}
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          onOrganizationUpdated={loadOrganizations}
        />

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete Organisation"
          message={`Are you sure you want to delete "${selectedOrganization?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmColor="error"
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteDialog}
        />
      </Box>
    </Container>
  );
}